import { createI18n } from 'vue-i18n'
import axios from 'axios'
import merge from 'lodash/merge'
import db from '@/firebase/init'
import { journeyVerboseLog } from '@/helpers/journeyVerboseLog'
import { guardDateFormats } from '@/helpers/dateFormat'

// Cache shipped `/locales/*.json` only; Firestore uiTranslations are merged on every load.
const bundledCache = new Map()
const BUNDLED_CACHE_DURATION = 2 * 60 * 60 * 1000 // 2 hours
const STORAGE_BUCKET =
  process.env.VUE_APP_FIREBASE_STORAGE_BUCKET || 'the-journey-dev.firebasestorage.app'

const FIRESTORE_METADATA_FIELDS = new Set([
  'createdAt',
  'updatedAt',
  'sourceLanguage',
  'autoTranslated',
  'status',
  'lastExported',
  'exportStatus',
  'exportError',
])

/**
 * Fetch bundled locale JSON with retries (first navigation after a cross-site redirect
 * can occasionally drop or race the XHR; refresh works because the connection is warm).
 */
async function fetchBundledLocaleJson(locale) {
  if (typeof window === 'undefined') {
    return null
  }
  const needsLoginScreen = locale === 'en' || locale === 'pt'
  const maxAttempts = 3
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const res = await fetch(`/locales/${locale}.json`, {
        cache: 'no-store',
        credentials: 'same-origin',
      })
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }
      const data = await res.json()
      if (
        data &&
        typeof data === 'object' &&
        Object.keys(data).length > 0 &&
        (!needsLoginScreen ||
          (data.loginScreen && typeof data.loginScreen === 'object'))
      ) {
        return data
      }
    } catch (e) {
      if (attempt === maxAttempts - 1) {
        console.warn(
          `[i18n] /locales/${locale}.json failed after ${maxAttempts} attempts`,
          e
        )
      }
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, 200 * (attempt + 1)))
    }
  }
  return null
}

async function fetchStorageLocaleJson(locale) {
  try {
    journeyVerboseLog(
      `[i18n] Loading translations for ${locale} from Firebase Storage`
    )
    const response = await axios.get(
      `https://firebasestorage.googleapis.com/v0/b/${encodeURIComponent(
        STORAGE_BUCKET
      )}/o/locales%2F${locale}.json?alt=media&t=${Date.now()}`
    )
    const data = response.data
    if (data && typeof data === 'object' && Object.keys(data).length > 0) {
      return data
    }
  } catch (error) {
    console.warn(
      `Failed to load locale ${locale} from Firebase Storage:`,
      error
    )
  }
  return null
}

async function getBundledMessages(locale) {
  const cacheKey = `bundled_${locale}`
  const cached = bundledCache.get(cacheKey)
  if (cached && Date.now() - cached.timestamp < BUNDLED_CACHE_DURATION) {
    journeyVerboseLog(`[i18n] Using cached bundled JSON for ${locale}`)
    return cached.data
  }

  let bundled = await fetchBundledLocaleJson(locale)
  if (bundled) {
    journeyVerboseLog(
      `[i18n] Loaded ${locale} from /locales (${Object.keys(bundled).length} top-level keys)`
    )
  } else {
    bundled = await fetchStorageLocaleJson(locale)
  }

  if (bundled && typeof bundled === 'object') {
    bundledCache.set(cacheKey, {
      data: bundled,
      timestamp: Date.now(),
    })
    return bundled
  }

  return null
}

/** vue-i18n placeholders must use ASCII names (e.g. {title}, not {título}). */
const PLACEHOLDER_NORMALIZATIONS = [
  [/\{título\}/g, '{title}'],
]

function normalizeTranslationString(value) {
  if (typeof value !== 'string') {
    return value
  }
  let normalized = value
  for (const [pattern, replacement] of PLACEHOLDER_NORMALIZATIONS) {
    normalized = normalized.replace(pattern, replacement)
  }
  return normalized
}

function normalizeTranslationTree(value) {
  if (typeof value === 'string') {
    return normalizeTranslationString(value)
  }
  if (Array.isArray(value)) {
    return value.map(normalizeTranslationTree)
  }
  if (value && typeof value === 'object') {
    const out = {}
    for (const [key, child] of Object.entries(value)) {
      out[key] = normalizeTranslationTree(child)
    }
    return out
  }
  return value
}

/**
 * Admin-edited strings in Firestore `uiTranslations/{locale}` override shipped JSON.
 * Matches merge order used in LanguageTranslations.vue (bundled base, Firestore wins).
 */
async function fetchUiTranslationsFromFirestore(locale) {
  try {
    const doc = await db.collection('uiTranslations').doc(locale).get()
    if (!doc.exists) {
      return null
    }

    const data = doc.data()
    const raw =
      data?.translations != null && typeof data.translations === 'object'
        ? data.translations
        : data

    if (!raw || typeof raw !== 'object') {
      return null
    }

    const filtered = {}
    for (const [key, value] of Object.entries(raw)) {
      if (!FIRESTORE_METADATA_FIELDS.has(key)) {
        filtered[key] = value
      }
    }

    const normalized =
      Object.keys(filtered).length > 0
        ? normalizeTranslationTree(filtered)
        : null

    return normalized
  } catch (error) {
    console.warn(
      `[i18n] Could not load uiTranslations/${locale} from Firestore:`,
      error
    )
    return null
  }
}

function mergeBundledWithFirestore(bundled, firestoreOverlay) {
  const base =
    bundled && typeof bundled === 'object' ? bundled : {}
  const overlay =
    firestoreOverlay && typeof firestoreOverlay === 'object'
      ? firestoreOverlay
      : {}
  return guardDateFormats(merge({}, base, overlay), base)
}

export const i18n = createI18n({
  legacy: false, // 👈 switch to composition API
  globalInjection: true, // 👈 still lets you use $t in templates
  locale: 'en',
  fallbackLocale: 'en',
  messages: {},
})

// Watch for locale changes and load translations automatically
i18n.global.locale.value = 'en' // Initialize with English

// Function to handle locale changes
export async function changeLocale(locale) {
  journeyVerboseLog(
    `[i18n] changeLocale called with: ${locale}, current: ${i18n.global.locale.value}`
  )

  // Check if locale is already set to avoid unnecessary work
  if (i18n.global.locale.value === locale) {
    journeyVerboseLog(`[i18n] Locale ${locale} is already set, skipping change`)
    return
  }

  try {
    journeyVerboseLog(`[i18n] Loading messages for ${locale}...`)
    const messages = await loadLocaleMessages(locale)
    journeyVerboseLog(
      `[i18n] Loaded ${
        Object.keys(messages).length
      } translation keys for ${locale}`
    )

    i18n.global.setLocaleMessage(locale, messages)
    i18n.global.locale.value = locale
    journeyVerboseLog(`[i18n] Successfully switched to locale: ${locale}`)
  } catch (error) {
    console.error(`[i18n] Error switching to locale ${locale}:`, error)
  }
}

export async function loadLocaleMessages(locale) {
  const bundled = await getBundledMessages(locale)
  const firestoreOverlay = await fetchUiTranslationsFromFirestore(locale)
  const messages = mergeBundledWithFirestore(
    bundled || {},
    firestoreOverlay || {}
  )

  if (Object.keys(messages).length > 0) {
    journeyVerboseLog(
      `[i18n] Resolved ${locale} messages (bundled: ${bundled ? 'yes' : 'no'}, Firestore overlay: ${firestoreOverlay ? 'yes' : 'no'})`
    )
    return messages
  }

  console.error(`[i18n] No translations available for locale: ${locale}`)
  return {}
}

export async function refreshLocaleMessages(locale) {
  clearTranslationCache(locale)
  const messages = await loadLocaleMessages(locale)
  if (messages && Object.keys(messages).length > 0) {
    i18n.global.setLocaleMessage(locale, messages)
    journeyVerboseLog(`[i18n] Refreshed vue-i18n messages for ${locale}`)
  }
  return messages
}

export function clearTranslationCache(locale = null) {
  if (locale) {
    bundledCache.delete(`bundled_${locale}`)
    journeyVerboseLog(`[i18n] Cleared bundled cache for ${locale}`)
  } else {
    bundledCache.clear()
    journeyVerboseLog('[i18n] Cleared all bundled translation cache')
  }
}

async function handleTranslationsUpdated(event) {
  const lang = event?.detail?.language
  if (!lang || typeof lang !== 'string') {
    return
  }
  try {
    await refreshLocaleMessages(lang.toLowerCase())
  } catch (error) {
    console.warn(
      `[i18n] Failed to refresh ${lang} after translationsUpdated:`,
      error
    )
  }
}

// Make clearTranslationCache available globally for debugging; refresh on admin save.
if (typeof window !== 'undefined') {
  window.clearTranslationCache = clearTranslationCache
  window.addEventListener('translationsUpdated', handleTranslationsUpdated)
}
